import { getRequiredAuthType } from '@shared/helpers/route-auth-map/routeAuthMap';

import { AuthStrategyResult } from './useAuthStrategy.types';

/**
 * Checks if the authenticated user's auth type is incompatible with the route
 *
 * Strict auth requirements:
 * - /train/* → B2C only (isInternalUser must be false)
 * - /instruct/* → B2E only (isInternalUser must be true)
 * - Other routes → no strict requirement
 *
 * @param path - Current URL path
 * @param isInternalUser - Whether the authenticated user is a B2E (internal) user
 * @returns true if there is a mismatch between auth type and route requirement
 */
export function isAuthTypeMismatch(
  path: string,
  isInternalUser: boolean
): boolean {
  const requiredAuth = getRequiredAuthType(path);
  if (!requiredAuth) return false;
  // B2C route (train) + internal user = mismatch
  // B2E route (instruct) + external user = mismatch
  return requiredAuth === 'b2c' ? isInternalUser : !isInternalUser;
}

/**
 * Determines the auth strategy based on URL path pattern
 *
 * Module-Specific Authentication:
 * - `/train/*` → B2C only (customers/trainees), stores deep link
 * - `/instruct/*` → B2E only (CAE employees), stores deep link
 * - `/manage/*` → Redirect to landing (security - don't expose org names)
 * - `/home` → B2C only (visitors), stores deep link
 *
 * @param path - The URL path to determine strategy for
 * @returns Auth strategy details
 */
export function determineAuthStrategy(path: string): AuthStrategyResult {
  const requiredAuth = getRequiredAuthType(path);

  if (requiredAuth === 'b2c' && path.startsWith('/train')) {
    return {
      authType: 'b2c',
      shouldStoreDeepLink: true,
      description: 'Train module - B2C login with deep link',
    };
  }

  if (requiredAuth === 'b2e' && path.startsWith('/instruct')) {
    return {
      authType: 'b2e',
      shouldStoreDeepLink: true,
      description: 'Instruct module - B2E login with deep link',
    };
  }

  if (path.startsWith('/login/internal')) {
    return {
      authType: 'b2e',
      shouldStoreDeepLink: false,
      description: 'Internal login - B2E trampoline',
    };
  }

  if (path.startsWith('/manage/sf-redirect')) {
    return {
      authType: 'b2e',
      shouldStoreDeepLink: false,
      description: 'Salesforce redirect - B2E orchestrator',
    };
  }

  if (path.startsWith('/manage')) {
    // Manage: store deep link (URLs use org IDs, not names)
    return {
      authType: 'b2c',
      shouldStoreDeepLink: true,
      description: 'Manage module - B2C login with deep link',
    };
  }

  if (path.startsWith('/home')) {
    return {
      authType: 'b2c',
      shouldStoreDeepLink: true,
      description: 'Visitor/Common flow - B2C login with deep link',
    };
  }

  // Default fallback
  return {
    authType: 'b2c',
    shouldStoreDeepLink: true,
    description: 'Unknown route - default B2C login',
  };
}

/**
 * Get returnUrl from sessionStorage (set by interceptor on session expiry)
 * This allows preserving the user's location when their session expires
 */
export function getReturnUrlFromStorage(): string | null {
  const returnUrl = sessionStorage.getItem('returnUrl');
  if (returnUrl) {
    // Clear it after reading to prevent stale redirects
    sessionStorage.removeItem('returnUrl');
  }
  return returnUrl;
}
